/**
 * RevenueExpensesWidget — dashboard card under the Cash Flow Forecast.
 *
 * Shows gross revenue + gross expenses + net for a flexible window (MTD,
 * QTD, YTD, 7d, 30d, 90d, last month, custom) with a side-by-side
 * comparison to either the prior equivalent period OR the same period
 * last year. Excludes unposted transactions and surfaces an inline
 * disclaimer so users don't think the totals are wrong.
 *
 * Styling deliberately mirrors CashFlowForecastWidget so the two cards
 * sit together as a visual pair on the Dashboard.
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  ArrowRight,
  ChevronDown,
  Loader2,
  AlertCircle,
  Info,
  CalendarRange,
} from 'lucide-react';
import { apiFetch } from '../../fetchInterceptor';
import { cn } from '../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Input } from '../ui/input';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
    .format(n || 0);

const fmtPct = (p) => {
  if (p === null || p === undefined) return '—';
  const sign = p >= 0 ? '+' : '−';
  return `${sign}${Math.abs(p).toFixed(0)}%`;
};

/**
 * Small gradient icon emblem — same visual language as the Money
 * Snapshot tiles on the Dashboard, so the widget feels first-party.
 * Tones map to icon + gradient pairs:
 *   emerald → revenue (TrendingUp)
 *   rose    → expenses (TrendingDown)
 *   indigo  → net (Wallet)
 */
const TileIconBadge = ({ tone = 'emerald', size = 'sm' }) => {
  const variants = {
    emerald: { grad: 'from-emerald-500 to-teal-600', Icon: TrendingUp },
    rose:    { grad: 'from-rose-500 to-orange-600',  Icon: TrendingDown },
    indigo:  { grad: 'from-indigo-500 to-violet-600', Icon: Wallet },
    zinc:    { grad: 'from-zinc-400 to-zinc-600',     Icon: Wallet },
  };
  const { grad, Icon } = variants[tone] || variants.emerald;
  const sizing = size === 'lg'
    ? { box: 'w-10 h-10 rounded-xl', icon: 'w-5 h-5' }
    : { box: 'w-7 h-7 rounded-lg', icon: 'w-3.5 h-3.5' };
  return (
    <div
      className={cn(
        'bg-gradient-to-br shadow-sm flex items-center justify-center text-white flex-shrink-0',
        grad,
        sizing.box,
      )}
      aria-hidden="true"
    >
      <Icon className={sizing.icon} strokeWidth={2.25} />
    </div>
  );
};

/** Status pill for delta values with an upward / downward chevron.
    When pct is null (prior baseline ~$0), we still show something
    actionable instead of "no prior data": "+new" if the current
    period has activity, "no change" if both periods are empty. */
const DeltaPill = ({ pct, tone, current }) => {
  if (pct === null || pct === undefined) {
    const hasCurrent = Math.abs(Number(current || 0)) >= 1;
    if (!hasCurrent) {
      return <span className="text-[11px] text-muted-foreground" data-testid="delta-pill">no change vs prior</span>;
    }
    const positive = Number(current) > 0;
    const treatedAsPositive = tone === 'expense' ? !positive : positive;
    return (
      <span
        data-testid="delta-pill"
        className={cn(
          'inline-flex items-center gap-1 text-[11px] font-semibold px-1.5 py-0.5 rounded',
          treatedAsPositive
            ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
            : 'bg-rose-500/10 text-rose-700 dark:text-rose-300',
        )}
      >
        {positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
        new
      </span>
    );
  }
  const positive = pct >= 0;
  // For expenses, an INCREASE is bad — invert the visual tone.
  const treatedAsPositive = tone === 'expense' ? !positive : positive;
  return (
    <span
      data-testid="delta-pill"
      className={cn(
        'inline-flex items-center gap-1 text-[11px] font-semibold px-1.5 py-0.5 rounded',
        treatedAsPositive
          ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
          : 'bg-rose-500/10 text-rose-700 dark:text-rose-300',
      )}
    >
      {positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {fmtPct(pct)}
    </span>
  );
};

/** Single side-by-side comparison tile. */
const StatTile = ({ icon, label, current, prior, deltaPct, deltaTone = 'revenue', accent = 'emerald' }) => {
  const accentText = {
    emerald: 'text-emerald-700 dark:text-emerald-300',
    rose: 'text-rose-700 dark:text-rose-300',
    indigo: 'text-indigo-700 dark:text-indigo-300',
  }[accent] || 'text-emerald-700 dark:text-emerald-300';

  return (
    <div className="rounded-xl border bg-card p-4 flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
        </div>
        <DeltaPill pct={deltaPct} tone={deltaTone} current={current} />
      </div>
      <div className={cn('text-2xl font-semibold tabular-nums', accentText)}>
        {fmt(current)}
      </div>
      <div className="text-[11px] text-muted-foreground tabular-nums">
        was {fmt(prior)}
      </div>
    </div>
  );
};

export default function RevenueExpensesWidget({ workspaceId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [range, setRange] = useState('mtd');
  const [compare, setCompare] = useState('prior_period');
  const [customOpen, setCustomOpen] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async (r, c, s, e) => {
    if (!workspaceId) return null;
    try {
      const params = new URLSearchParams({
        workspace_id: workspaceId,
        range: r,
        compare: c,
      });
      if (r === 'custom' && s && e) {
        params.set('start_date', s);
        params.set('end_date', e);
      }
      const res = await apiFetch(`${API_URL}/api/dashboard/command-center/revenue-expenses?${params}`);
      if (!res.ok) throw new Error('fetch failed');
      return await res.json();
    } catch {
      return null;
    }
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      // First-load uses range="auto" so the backend silently pivots to
      // the workspace's most-recent populated month when the standard
      // 30-day window is empty (e.g. historical books w/ no recent
      // activity). Once the user picks a specific preset, we keep it.
      const j = await fetchData('auto', 'prior_period');
      if (cancelled) return;
      if (j) {
        setData(j);
        // Sync local state so the dropdown reflects the backend pivot.
        if (j.range && j.range !== 'auto') setRange(j.range);
        if (j.range === 'custom') {
          setCustomStart(j.start_date || '');
          setCustomEnd(j.end_date || '');
        }
      } else {
        setError(true);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [workspaceId, fetchData]);

  const refresh = async (r, c, s, e) => {
    setRefreshing(true);
    const j = await fetchData(r, c, s, e);
    if (j) setData(j);
    setRefreshing(false);
  };

  const changeRange = (newRange) => {
    if (newRange === 'custom') { setCustomOpen(true); return; }
    setRange(newRange);
    refresh(newRange, compare);
  };

  const changeCompare = (newCompare) => {
    setCompare(newCompare);
    refresh(range, newCompare);
  };

  const applyCustom = () => {
    if (!customStart || !customEnd) return;
    setRange('custom');
    setCustomOpen(false);
    refresh('custom', compare, customStart, customEnd);
  };

  if (loading) {
    return (
      <div className="rounded-2xl border bg-card p-6 animate-pulse" data-testid="rev-exp-loading">
        <div className="h-5 w-48 bg-muted rounded mb-4" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[1, 2, 3].map(i => <div key={i} className="h-24 bg-muted rounded-lg" />)}
        </div>
      </div>
    );
  }

  if (error || !data) return null;

  const currentRangeLabel =
    data.ranges_available.find(r => r.key === data.range)?.label || data.range_label;
  const currentCompareLabel =
    data.compares_available.find(c => c.key === data.compare)?.label || data.compare_label;

  return (
    <div
      className="rounded-2xl border bg-gradient-to-br from-emerald-50/40 via-white to-white dark:from-emerald-950/20 dark:via-zinc-900 dark:to-zinc-900 p-6 space-y-4"
      data-testid="revenue-expenses-widget"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <TileIconBadge tone="emerald" size="lg" />
          <div>
            <h3 className="font-semibold text-base flex items-center gap-1.5">
              Revenue & Expenses
              {refreshing && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
            </h3>
            <p className="text-xs text-muted-foreground">
              {data.start_date} → {data.end_date}
            </p>
            {/* Auto-pivot indicator — backend silently jumped to the
                user's most recent populated month because the standard
                window was empty. Tells the user this isn't a current
                snapshot, with a one-click way back to the 30-day view. */}
            {data.auto_pivoted && (
              <button
                type="button"
                onClick={() => changeRange('30d')}
                className="mt-1 inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 dark:text-amber-300 hover:underline"
                data-testid="rev-exp-auto-pivot-reset"
              >
                <AlertCircle className="w-3 h-3" />
                Showing your latest activity period — switch back to last 30 days
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Period dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="rev-exp-range-btn">
                <CalendarRange className="w-3.5 h-3.5 mr-1.5" />
                {currentRangeLabel}
                <ChevronDown className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Period
              </DropdownMenuLabel>
              {data.ranges_available.map((r) => (
                <DropdownMenuItem
                  key={r.key}
                  onClick={() => changeRange(r.key)}
                  data-testid={`rev-exp-range-${r.key}`}
                  className={cn(data.range === r.key && 'bg-accent')}
                >
                  {r.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Compare dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="rev-exp-compare-btn">
                vs {currentCompareLabel}
                <ChevronDown className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Compare against
              </DropdownMenuLabel>
              {data.compares_available.map((c) => (
                <DropdownMenuItem
                  key={c.key}
                  onClick={() => changeCompare(c.key)}
                  data-testid={`rev-exp-compare-${c.key}`}
                  className={cn(data.compare === c.key && 'bg-accent')}
                >
                  {c.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Side-by-side tiles */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <StatTile
          icon={<TileIconBadge tone="emerald" />}
          label="Revenue"
          current={data.current.revenue}
          prior={data.prior.revenue}
          deltaPct={data.deltas.revenue_pct}
          deltaTone="revenue"
          accent="emerald"
        />
        <StatTile
          icon={<TileIconBadge tone="rose" />}
          label="Expenses"
          current={data.current.expenses}
          prior={data.prior.expenses}
          deltaPct={data.deltas.expenses_pct}
          deltaTone="expense"
          accent="rose"
        />
        <StatTile
          icon={<TileIconBadge tone="indigo" />}
          label="Net"
          current={data.current.net}
          prior={data.prior.net}
          deltaPct={data.deltas.net_pct}
          deltaTone="revenue"
          accent={data.current.net >= 0 ? 'emerald' : 'rose'}
        />
      </div>

      {/* Prior-period context line */}
      <p className="text-[11px] text-muted-foreground">
        Comparison window: <span className="font-medium text-foreground">{data.prior_start_date || '—'} → {data.prior_end_date || '—'}</span>
      </p>

      {/* No-activity-in-window hint — when both revenue and expenses are
          $0 in the selected window but the workspace DOES have posted
          P&L activity in another month, surface a one-click jump to
          that month so the widget shows real numbers instead of $0/$0/$0. */}
      {data.suggested_range && (
        <div
          className="rounded-lg border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-950/30 px-3 py-2.5 text-[12px] flex items-center justify-between gap-3"
          data-testid="rev-exp-suggested-range-banner"
        >
          <div className="flex items-start gap-2 min-w-0">
            <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-amber-900 dark:text-amber-200 leading-snug">
              No P&L activity in this window. <strong>{data.suggested_range.hint}</strong> to see your most recent numbers.
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="border-amber-300 text-amber-900 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-200 dark:hover:bg-amber-900/40 flex-shrink-0"
            onClick={() => {
              setRange('custom');
              setCustomStart(data.suggested_range.start_date);
              setCustomEnd(data.suggested_range.end_date);
              refresh('custom', compare, data.suggested_range.start_date, data.suggested_range.end_date);
            }}
            data-testid="rev-exp-suggested-range-apply"
          >
            Jump to {data.suggested_range.label}
          </Button>
        </div>
      )}

      {/* Unposted disclaimer — surfaces only when there's actually something
        * unposted in the window, so the message isn't permanent noise. */}
      {data.unposted && data.unposted.total > 0 && (
        <div
          className="rounded-lg border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-950/30 px-3 py-2.5 text-[12px] space-y-2"
          data-testid="rev-exp-unposted-disclaimer"
        >
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-amber-900 dark:text-amber-200">
              <strong>{data.unposted.total} unposted item{data.unposted.total === 1 ? '' : 's'}</strong> in this window
              {data.unposted.uncategorized_bank > 0 && <> · {data.unposted.uncategorized_bank} uncategorized bank txns</>}
              {data.unposted.draft_invoices > 0 && <> · {data.unposted.draft_invoices} draft invoices</>}
              {data.unposted.draft_bills > 0 && <> · {data.unposted.draft_bills} draft bills</>}
              {' '}— not included in the totals above.{' '}
              <Popover>
                <PopoverTrigger asChild>
                  <button className="underline underline-offset-2 font-medium hover:text-amber-700">
                    Why?
                  </button>
                </PopoverTrigger>
                <PopoverContent className="text-xs max-w-xs" align="start">
                  <p className="font-semibold mb-1 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> About unposted items
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    Comparison totals only include <strong>posted</strong> journal entries.
                    Uncategorized bank transactions, draft invoices, and draft bills are
                    excluded so the numbers match your General Ledger exactly. Categorize
                    or post the pending items to see them flow through here.
                  </p>
                </PopoverContent>
              </Popover>
            </div>
          </div>
          {/* Actionable deep-links — let the user fix the underlying cause
              of $0 revenue/expenses in one click instead of hunting for it. */}
          <div className="flex flex-wrap gap-2 pl-6">
            {data.unposted.uncategorized_bank > 0 && (
              <button
                onClick={() => navigate('/banking?tab=accounting-hub&filter=uncategorized')}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-amber-500/15 hover:bg-amber-500/25 text-amber-800 dark:text-amber-200 font-medium transition-colors"
                data-testid="rev-exp-fix-uncategorized-btn"
              >
                Categorize {data.unposted.uncategorized_bank} transaction{data.unposted.uncategorized_bank === 1 ? '' : 's'}
                <ArrowRight className="w-3 h-3" />
              </button>
            )}
            {data.unposted.draft_invoices > 0 && (
              <button
                onClick={() => navigate('/invoices?status=draft')}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-amber-500/15 hover:bg-amber-500/25 text-amber-800 dark:text-amber-200 font-medium transition-colors"
                data-testid="rev-exp-fix-draft-invoices-btn"
              >
                Send {data.unposted.draft_invoices} draft invoice{data.unposted.draft_invoices === 1 ? '' : 's'}
                <ArrowRight className="w-3 h-3" />
              </button>
            )}
            {data.unposted.draft_bills > 0 && (
              <button
                onClick={() => navigate('/bills?status=draft')}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-amber-500/15 hover:bg-amber-500/25 text-amber-800 dark:text-amber-200 font-medium transition-colors"
                data-testid="rev-exp-fix-draft-bills-btn"
              >
                Post {data.unposted.draft_bills} draft bill{data.unposted.draft_bills === 1 ? '' : 's'}
                <ArrowRight className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Footer link to the full P&L report */}
      <button
        onClick={() => navigate('/reports?report=profit-loss')}
        className="group flex items-center gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-400 hover:text-emerald-600"
        data-testid="rev-exp-view-pnl-btn"
      >
        View full Profit & Loss
        <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
      </button>

      {/* Custom range dialog */}
      <Dialog open={customOpen} onOpenChange={setCustomOpen}>
        <DialogContent data-testid="rev-exp-custom-range-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Custom date range</DialogTitle>
            <DialogDescription>
              Pick a window — the comparison will follow your selection (prior equivalent or prior year).
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium mb-1 block">Start</label>
              <Input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                data-testid="rev-exp-custom-start"
              />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block">End</label>
              <Input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                data-testid="rev-exp-custom-end"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setCustomOpen(false)}>Cancel</Button>
            <Button
              onClick={applyCustom}
              disabled={!customStart || !customEnd || customStart > customEnd}
              data-testid="rev-exp-custom-apply"
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
            >
              Apply range
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
